import { useLayoutEffect, useRef } from 'react';
import { motionEase, type QueueMotion } from './queueMotion';

function layoutPosition(node: HTMLElement) {
  const shift = new DOMMatrixReadOnly(getComputedStyle(node).transform);
  const rect = node.getBoundingClientRect();
  return {
    x: rect.left + window.scrollX - shift.m41,
    y: rect.top + window.scrollY - shift.m42,
  };
}

// Move existing coins into their new slots; keep hover transforms on the inner button.
export function useRailLayout(ids: string, motion: QueueMotion) {
  const list = useRef<HTMLUListElement>(null);
  const positions = useRef(new Map<string, { x: number; y: number }>());
  const animations = useRef(new Map<string, Animation>());
  useLayoutEffect(() => {
    const next = new Map<string, { x: number; y: number }>();
    const reduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    for (const node of list.current?.children ?? []) {
      if (!(node instanceof HTMLElement)) continue;
      const id = node.dataset.coinId!;
      const shift = new DOMMatrixReadOnly(getComputedStyle(node).transform);
      const target = layoutPosition(node);
      const previous = positions.current.get(id);
      animations.current.get(id)?.cancel();
      animations.current.delete(id);
      if (previous && !reduced) {
        const dx = previous.x + shift.m41 - target.x;
        const dy = previous.y + shift.m42 - target.y;
        if (Math.abs(dx) + Math.abs(dy) > 0.5) {
          animations.current.set(
            id,
            node.animate(
              [
                { transform: `translate(${dx}px, ${dy}px)` },
                { transform: 'translate(0, 0)' },
              ],
              {
                duration: motion.tapeMs,
                easing: motionEase(motion.tapeBounce),
              },
            ),
          );
        }
      }
      next.set(id, target);
    }
    for (const [id, animation] of animations.current) {
      if (!next.has(id)) {
        animation.cancel();
        animations.current.delete(id);
      }
    }
    positions.current = next;
  }, [ids, motion.tapeMs, motion.tapeBounce]);
  useLayoutEffect(() => {
    // Hover changes real slot sizes. Keep the next arrival/reorder's FLIP origin
    // current, without starting another animation for hover-driven movement.
    const node = list.current;
    if (!node) return;
    const observer = new ResizeObserver(() => {
      for (const child of node.children) {
        if (child instanceof HTMLElement)
          positions.current.set(child.dataset.coinId!, layoutPosition(child));
      }
    });
    observer.observe(node);
    for (const child of node.children) observer.observe(child);
    return () => observer.disconnect();
  }, [ids]);
  useLayoutEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const reset = () => {
      for (const animation of animations.current.values()) animation.cancel();
      animations.current.clear();
      positions.current.clear();
    };
    const node = list.current;
    window.addEventListener('resize', reset);
    node?.parentElement?.addEventListener('scroll', reset);
    reduced.addEventListener('change', reset);
    return () => {
      reset();
      window.removeEventListener('resize', reset);
      node?.parentElement?.removeEventListener('scroll', reset);
      reduced.removeEventListener('change', reset);
    };
  }, []);
  return list;
}
